import classNames from "classnames";
import React, { useEffect, useState } from "react";
import { Controller, useForm } from "react-hook-form";
import { Card, Col, Form, Label, Progress, Row } from "reactstrap";
import {
  BlockDes,
  BlockHeadContent,
  BlockTitle,
  Button,
  RSelect,
} from "../../../components/Component";
import { useDataFetch } from "../../../hooks/useDataFetch";
import Content from "../../../layout/content/Content";
import Head from "../../../layout/head/Head";
import { compressImage, formatBytes } from "../../../utils/file";
import { toast } from "react-toastify";

/** El que asigna el API cuando no se indica ninguno. */
const TIPO_POR_DEFECTO = "Voluntario";

const Trees = () => {
  const [deparments, setDepartments] = useState([]);
  const [departemtnSelected, setDepartmentSelected] = useState();
  const [cities, setCities] = useState([]);
  // { fase: "comprimiendo" | "subiendo", percent, originalSize, finalSize }
  const [upload, setUpload] = useState(null);
  const {
    register,
    handleSubmit,
    reset,
    control,
    setValue,
    formState: { errors },
  } = useForm({ defaultValues: { type: TIPO_POR_DEFECTO } });

  const formClass = classNames({
    "form-validate": true,
  });

  const { getData, deleteData, postMultipart, invalidateQuery } = useDataFetch();

  const convertToReactSelectOptions = (data) => {
    // Un objeto de error también es "truthy": hay que exigir un arreglo.
    if (!Array.isArray(data)) return [];

    return data.map((item) => {
      const values = Object.values(item);
      return {
        value: values[0],
        label: values[1],
      };
    });
  };

  const { data: volunteer_name } = getData(
    ["volunteers"],
    "/panel/volunteer-name",
    { enabled: true }
  );

  const { data: allianzer_area } = getData(
    ["allianzer_area"],
    "/panel/allianzer-areas",
    { enabled: true }
  );

  const { data: family } = getData(["familiy"], "/panel/family", {
    enabled: true,
  });

  const { data: treeLastCode, refetch: refetchLatCode } = getData(
    ["lastCode"],
    "/panel/trees/last/code",
    {
      enabled: true,
    }
  );

  const { data: species } = getData(["specia"], "/panel/specie", {
    enabled: true,
  });

  const { data: common_name } = getData(
    ["common-names"],
    "/panel/common-names",
    { enabled: true }
  );

  const { data: location } = getData(["location"], "/panel/location", {
    enabled: true,
  });

  // Antes eran tres opciones escritas aquí; ahora salen del catálogo.
  const { data: treeTypes } = getData(["tree-types"], "/panel/tree-types", {
    enabled: true,
  });

  const { mutate: createTree, isLoading } = postMultipart(
    "/panel/trees",
    {},
    {
      onUploadProgress: ({ percent }) =>
        setUpload((prev) => (prev ? { ...prev, percent } : prev)),
      onSuccess: (data) => {
        setUpload(null);
        if (!data?.error) {
          reset({
            department: null,
            city: null,
            photo_base64: null,
            type: TIPO_POR_DEFECTO,
            origin_distribution: null,
            family: null,
            species: null,
            common_name: null,
            volunteer_name: null,
            location: null,
            allianzer_area: null,
          });
          toast.success("Se guardo el árbol correctamente.");
          refetchLatCode();
        } else {
          toast.error("El código del árbol ya se encuentra en uso.");
        }
      },
      // El API responde 400 con el motivo (código en uso, tipo inexistente).
      // Antes solo se ocultaba la barra de progreso y el error no se veía.
      onError: (error) => {
        setUpload(null);
        toast.error(error?.message || "No fue posible guardar el árbol.");
      },
    }
  );

  useEffect(() => {
    fetchDeparment();
  }, []);

  useEffect(() => {
    if (treeLastCode) {
      setValue("code", treeLastCode?.last_code);
    }
  }, [treeLastCode]);

  const onFormSubmit = async (e) => {
    const { photo_base64: seleccion, ...resto } = e;

    const values = {
      ...resto,
      department: departemtnSelected.label,
      city: e.city.label,
      // Una opción elegida en el select, o el nombre por defecto si no se tocó.
      type: e.type?.label ?? e.type ?? TIPO_POR_DEFECTO,
      origin_distribution: e.origin_distribution.label,
      family: e.family.label,
      species: e.species.label,
      common_name: e.common_name.label,
      volunteer_name: e.volunteer_name.label,
      location: e.location.label,
      allianzer_area: e.allianzer_area?.label ?? "",
    };

    // Se envía como multipart en lugar de base64: evita inflar el archivo un
    // 33 % y no obliga al servidor a cargarlo entero en memoria.
    const formData = new FormData();
    Object.entries(values).forEach(([campo, valor]) => {
      if (valor === null || valor === undefined) return;
      formData.append(campo, valor);
    });

    const original = seleccion?.[0];
    if (original instanceof File) {
      setUpload({ fase: "comprimiendo", percent: 0 });
      let optimizada = original;
      try {
        optimizada = await compressImage(original);
      } catch (error) {
        // Si la compresión falla se sube la original: es preferible una
        // subida lenta a perder el registro.
        console.error("No se pudo optimizar la imagen:", error);
      }
      setUpload({
        fase: "subiendo",
        percent: 0,
        originalSize: original.size,
        finalSize: optimizada.size,
      });
      formData.append("photo", optimizada, optimizada.name);
    }

    createTree(formData);
  };

  const fetchDeparment = async () => {
    const response = await fetch("https://api-colombia.com/api/v1/Department", {
      method: "GET",
    });
    const result = await response.json();
    setDepartments(
      result.map((item) => ({
        value: item.id,
        label: item.name,
      }))
    );
  };

  const fetchCities = async (e) => {
    const id = e.value;
    const response = await fetch(
      `https://api-colombia.com/api/v1/Department/${id}/cities`,
      {
        method: "GET",
      }
    );
    const result = await response.json();
    setCities(
      result.map((item) => {
        return {
          value: item.name,
          label: item.name,
        };
      })
    );
  };

  return (
    <React.Fragment>
      <Head title="Crear Árbol"></Head>
      <Content>
        <Card className="p-4">
          <Form onSubmit={handleSubmit(onFormSubmit)}>
            <Row className="g-gs">
              <Col md="12">
                <BlockHeadContent>
                  <BlockTitle tag="h4">Crear árbol</BlockTitle>
                  <BlockDes></BlockDes>
                </BlockHeadContent>
              </Col>

              {/* Code */}
              <Col md="4">
                <div className="form-group">
                  <Label className="form-label" htmlFor="code">
                    Código
                  </Label>
                  <div className="form-control-wrap">
                    <input
                      type="text"
                      id="code"
                      {...register("code", { required: true })}
                      className="form-control"
                    />
                    {errors.code && (
                      <span className="invalid">Este campo es requerido</span>
                    )}
                  </div>
                </div>
              </Col>

              {/* Family */}
              <Col md="4">
                <div className="form-group">
                  <Label className="form-label" htmlFor="family">
                    Familia
                  </Label>
                  <Controller
                    name="family"
                    control={control}
                    render={({ field }) => (
                      <RSelect
                        {...field}
                        options={convertToReactSelectOptions(family)}
                        classNamePrefix="react-select"
                        placeholder="Seleccionar"
                      />
                    )}
                  />
                  {errors.family && (
                    <span className="invalid">Este campo es requerido</span>
                  )}
                </div>
              </Col>

              {[
                {
                  id: "species",
                  label: "Especie",
                  type: "select",
                  value: species,
                  required: true,
                },
                {
                  id: "common_name",
                  label: "Nombre común",
                  type: "select",
                  value: common_name,
                  required: true,
                },
                {
                  id: "origin_distribution",
                  label: "Distribución de origen",
                  type: "select",
                  value: [
                    { value: "Endémica", label: "Endémica" },
                    { value: "Nativa", label: "Nativa" },
                    { value: "Casi endémica", label: "Casi endémica" },
                  ],
                  required: true,
                },
                {
                  id: "type",
                  label: "Tipo",
                  type: "select",
                  value: treeTypes,
                  required: true,
                  // El valor del campo es el nombre del tipo (o la opción
                  // elegida), no su id: así el defecto se ve seleccionado.
                  porNombre: true,
                },
                {
                  id: "allianzer_area",
                  label: "Área Allianzer",
                  type: "select",
                  value: allianzer_area,
                  required: false,
                },
                {
                  id: "volunteer_name",
                  label: "Nombre del voluntario",
                  type: "select",
                  value: volunteer_name,
                  required: false,
                },
                {
                  id: "department",
                  label: "Departamento",
                  type: "select",
                  required: true,
                  value: deparments,
                },
                {
                  id: "city",
                  label: "Ciudad",
                  type: "select",
                  required: true,
                  value: cities,
                },
                {
                  id: "location",
                  label: "Ubicación",
                  type: "select",
                  value: location,
                  required: true,
                },
                {
                  id: "planting_date",
                  label: "Fecha de plantación",
                  type: "date",
                  required: true,
                },
                { id: "longitude", label: "Longitud", required: true },
                { id: "latitude", label: "Latitud", required: true },
              ].map(({ id, label, type = "text", value, required, porNombre }) => (
                <Col md="4" key={id}>
                  <div className="form-group">
                    <Label className="form-label" htmlFor={id}>
                      {label}
                    </Label>
                    {type && type === "select" && id != "department" ? (
                      <Controller
                        name={id}
                        control={control}
                        render={({ field }) => (
                          <RSelect
                            {...field}
                            value={convertToReactSelectOptions(value).find(
                              (option) =>
                                porNombre
                                  ? option?.label ===
                                    (field?.value?.label ?? field?.value)
                                  : option?.value === field?.value
                            )}
                            options={convertToReactSelectOptions(value)}
                            classNamePrefix="react-select"
                            placeholder="Seleccionar"
                          />
                        )}
                      />
                    ) : type && type === "select" && id == "department" ? (
                      <Controller
                        name={id}
                        control={control}
                        render={({ field }) => (
                          <RSelect
                            {...field}
                            options={convertToReactSelectOptions(value)}
                            classNamePrefix="react-select"
                            placeholder="Seleccionar"
                            onChange={(e) => {
                              setDepartmentSelected(e);
                              fetchCities(e);
                            }}
                          />
                        )}
                      />
                    ) : (
                      <div className="form-control-wrap">
                        <input
                          type={type}
                          id={id}
                          {...register(id, { required })}
                          className="form-control"
                        />
                        {errors[id] && (
                          <span className="invalid">
                            Este campo es requerido
                          </span>
                        )}
                      </div>
                    )}
                  </div>
                </Col>
              ))}

              {/* Photo */}
              <Col md="4">
                <div className="form-group">
                  <Label className="form-label" htmlFor="photo_base64">
                    Foto
                  </Label>
                  <div className="form-control-wrap">
                    <input
                      type="file"
                      accept="image/*"
                      id="photo_base64"
                      {...register("photo_base64", { required: true })}
                      className="form-control"
                    />
                    {errors.photo_base64 && (
                      <span className="invalid">
                        Este campo es requerido y debe ser una imagen
                      </span>
                    )}
                  </div>
                </div>
              </Col>

              {/* Progreso de la subida */}
              {upload && (
                <Col md="12">
                  <div className="form-group">
                    <div className="d-flex justify-content-between mb-1">
                      <small className="text-soft">
                        {upload.fase === "comprimiendo"
                          ? "Optimizando la imagen..."
                          : `Subiendo... ${upload.percent ?? 0}%`}
                      </small>
                      {upload.originalSize != null && (
                        <small className="text-soft">
                          {formatBytes(upload.originalSize)} →{" "}
                          {formatBytes(upload.finalSize)}
                        </small>
                      )}
                    </div>
                    <Progress
                      animated={upload.fase === "comprimiendo"}
                      value={
                        upload.fase === "comprimiendo" ? 100 : upload.percent ?? 0
                      }
                    />
                  </div>
                </Col>
              )}

              {/* Submit */}
              <Col md="12">
                <div className="form-group">
                  <Button color="primary" size="lg" disabled={isLoading}>
                    {isLoading ? "Guardando..." : "Guardar"}
                  </Button>
                </div>
              </Col>
            </Row>
          </Form>
        </Card>
      </Content>
    </React.Fragment>
  );
};

export default Trees;
